import { classNames } from 'primereact/utils';
import { LeftArrowIcon, RightArrowIcon } from '@repo/atomic-components';
import { Table } from '@tanstack/react-table';

interface PaginationControlsProps<T = unknown> {
  table: Table<T>;
  pages: (number | 'dots-before' | 'dots-after')[];
  currentPage: number;
  arrowIconClassName?: string;
  arrowTextClassName?: string;
  pageButtonTextClassName?: string;
  pageButtonActiveClassName?: string;
  pageButtonInactiveClassName?: string;
}

const PaginationControls = <T,>({
  table,
  pages,
  currentPage,
  arrowIconClassName,
  arrowTextClassName,
  pageButtonTextClassName,
  pageButtonActiveClassName,
  pageButtonInactiveClassName,
}: PaginationControlsProps<T>) => {
  return (
    <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
      <button
        className={classNames(
          'flex h-8 w-[26px] items-center justify-center',
          'text-[0.875rem]/5 font-[400]',
          'cursor-pointer transition-opacity duration-200',
          'hover:opacity-70 active:opacity-50',
          arrowTextClassName ?? 'text-fletchers-green-800',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:opacity-50',
          arrowIconClassName,
        )}
        onClick={() => table.previousPage()}
        disabled={!table.getCanPreviousPage()}
        aria-label="Previous page"
      >
        <LeftArrowIcon />
      </button>

      {pages.map((p, index) =>
        p === 'dots-before' || p === 'dots-after' ? (
          <span className={classNames('px-2 py-0.5', 'text-[0.875rem]/5 font-[400]')} key={`dots-${index}`}>
            ...
          </span>
        ) : (
          <button
            key={p}
            className={classNames(
              'min-h-8 min-w-[31px] rounded px-2.5 py-1',
              'text-[0.75rem] font-semibold',
              'cursor-pointer transition-colors duration-200',
              'flex items-center justify-center',
              {
                [pageButtonTextClassName ?? 'text-fletchers-green-800']: true,
                [pageButtonActiveClassName ?? 'bg-fletchers-green-100 font-bold']: p === currentPage,
                [pageButtonInactiveClassName ?? 'hover:bg-fletchers-green-100 active:bg-fletchers-green-150']:
                  p !== currentPage,
              },
            )}
            onClick={() => table.setPageIndex(Number(p) - 1)}
            aria-label={`Go to page ${p}`}
            aria-current={p === currentPage ? 'page' : undefined}
          >
            {p}
          </button>
        ),
      )}
      <button
        className={classNames(
          'flex h-8 w-[26px] items-center justify-center',
          'text-[0.875rem]/5 font-[400]',
          'cursor-pointer transition-opacity duration-200',
          'hover:opacity-70 active:opacity-50',
          arrowTextClassName ?? 'text-fletchers-green-800',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:opacity-50',
          arrowIconClassName,
        )}
        onClick={() => table.nextPage()}
        disabled={!table.getCanNextPage()}
        aria-label="Next page"
      >
        <RightArrowIcon />
      </button>
    </div>
  );
};

export default PaginationControls;
